import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Building2 } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { CentroCustoStat } from "../chartUtils";
import { PedidosModalContent, ModalEntityTitle } from "./PedidosModalContent";
import { BAR_HOVER_PROPS, TOOLTIP_CURSOR_BAND, barRowFromClick } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { payload: CentroCustoStat }[];
}
function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 max-w-[220px]">
      <p className="text-xs font-semibold text-slate-800 mb-2 leading-tight">{d.nome}</p>
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Qtd.</span>
          <span className="font-semibold">{formatNumber(d.quantidade)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Valor</span>
          <span className="font-semibold">{formatCurrency(d.valor)}</span>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Clique para ver detalhes</p>
    </div>
  );
}

const PALETTE = [
  "#B03858","#3b82f6","#10b981","#f59e0b","#8b5cf6",
  "#ec4899","#14b8a6","#f97316","#6366f1","#84cc16",
];

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    CentroCustoStat[];
  loading: boolean;
}

export function PedidosPorCentro({ data, loading }: Props) {
  const [selected, setSelected] = useState<CentroCustoStat | null>(null);
  const chartData = data.slice(0, 10);
  const height    = Math.max(260, Math.min(chartData.length * 44, 420));

  return (
    <>
      <ChartContainer
        title="Pedidos por Centro de Custo"
        subtitle={`Top ${Math.min(data.length, 10)} centros por valor total`}
        loading={loading}
        empty={!loading && data.length === 0}
        onExpand={() => setSelected(data[0] ?? null)}
        height={height}
      >
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 72, left: 8, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <YAxis
            type="category"
            dataKey="nomeAbrev"
            tick={{ fontSize: 11, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
            width={144}
          />
          <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
          <Bar
            {...BAR_HOVER_PROPS}
            dataKey="valor"
            name="Valor (R$)"
            radius={[0, 4, 4, 0]}
            maxBarSize={30}
            cursor="pointer"
            onClick={(item: unknown) => {
              const d = barRowFromClick<CentroCustoStat>(item);
              if (d) setSelected(d);
            }}
            label={{
              position: "right",
              formatter: (v: unknown) => `${(Number(v) / 1000).toFixed(0)}k`,
              fontSize: 11,
              fill: "#94a3b8",
            }}
          >
            {chartData.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <ModalEntityTitle
            icon={<Building2 className="w-4 h-4" />}
            label={selected.nome}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        )}
      >
        {selected && (
          <PedidosModalContent pedidos={selected.pedidos} hideCols={["centro"]} expandable />
        )}
      </Modal>
    </>
  );
}
